import { useMemo } from "react";
import { ICalendarEvent } from "@/interfaces/dashboard.interface";
import { CalendarEventBox } from "./calendar-event-box";
import { cn } from "@/lib/class-control";

interface MonthlyCalendarGridProps {
  currentDate: Date;
  events: ICalendarEvent[];
  onEventClick?: (event: ICalendarEvent) => void;
}

const weekDays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

// Column widths matching Figma exactly
const columnWidths = ["135px", "135px", "137px", "136px", "137px", "135px", "135px"];

export function MonthlyCalendarGrid({ currentDate, events, onEventClick }: MonthlyCalendarGridProps) {
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Get first day of month (0 = Sunday, 1 = Monday, etc.)
    const firstDay = new Date(year, month, 1).getDay();
    // Convert to Monday-based (0 = Monday, 6 = Sunday)
    const firstDayMonday = firstDay === 0 ? 6 : firstDay - 1;

    // Get days in current month
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Get days in previous month
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    // Add days from previous month
    for (let i = firstDayMonday - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, daysInPrevMonth - i),
      });
    }

    // Add days from current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day),
      });
    }

    // Add days from next month to complete the grid (35 days = 5 weeks)
    const remainingDays = 35 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day),
      });
    }

    return days;
  }, [currentDate]);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, ICalendarEvent[]>();
    events.forEach((event) => {
      const existing = map.get(event.date) || [];
      map.set(event.date, [...existing, event]);
    });
    return map;
  }, [events]);

  const getEventsForDate = (date: Date) => {
    const dateStr = date.toISOString().split("T")[0];
    return eventsByDate.get(dateStr) || [];
  };

  return (
    <div className="flex flex-col">
      {/* Header Row */}
      <div className="flex bg-[#7846FF]">
        {weekDays.map((day, index) => (
          <div
            key={day}
            className="flex h-[38px] items-center justify-center"
            style={{ width: columnWidths[index] }}
          >
            <p className="text-[14px] font-bold text-white">{day}</p>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex flex-col">
        {Array.from({ length: 5 }).map((_, weekIndex) => (
          <div key={weekIndex} className="flex">
            {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map((dayInfo, dayIndex) => {
              const dayEvents = getEventsForDate(dayInfo.date);

              return (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className="relative flex h-[115px] flex-col items-end gap-[29px] border border-[#3D3D3D]"
                  style={{ width: columnWidths[dayIndex] }}
                >
                  {/* Day Number */}
                  <div className="flex h-[39px] w-full items-center justify-center">
                    <p
                      className={cn(
                        "text-center text-[14px]",
                        dayInfo.isCurrentMonth ? "font-semibold text-white" : "font-normal text-white"
                      )}
                    >
                      {dayInfo.day}
                    </p>
                  </div>

                  {/* Events */}
                  {dayEvents.length > 0 && (
                    <div className="flex w-full flex-col gap-[2px]">
                      {dayEvents.slice(0, 1).map((event) => (
                        <CalendarEventBox
                          key={event.id}
                          title={event.title}
                          onClick={() => onEventClick?.(event)}
                        />
                      ))}
                      {dayEvents.length > 1 && (
                        <div className="px-[10px] pb-[5px]">
                          <p className="text-[10px] font-semibold text-white/70">
                            +{dayEvents.length - 1} mais
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
